import { useState } from 'react';
import {
  Button,
  PlusIcon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from '@playerbase/ui';
import {
  PUBLISHING_STORE_LABELS,
  STORES_BY_PLATFORM,
  type GamePlatform,
  type PublishingStore,
} from '@playerbase/types';

type AddDestinationPopoverProps = {
  platform: GamePlatform;
  existingStores: Set<PublishingStore>;
  onAdd: (store: PublishingStore) => void | Promise<void>;
};

export function AddDestinationPopover({
  platform,
  existingStores,
  onAdd,
}: AddDestinationPopoverProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const candidates = STORES_BY_PLATFORM[platform].filter(
    (store) => !existingStores.has(store),
  );

  const allAdded = candidates.length === 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={allAdded}
          aria-label={`Add publishing destination for ${platform}`}
        >
          <PlusIcon />
          Add destination
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="min-w-[14rem] p-1">
        {candidates.length === 0 ? (
          <p className="px-3 py-2 text-xs text-text-muted">
            All available stores added.
          </p>
        ) : (
          <ul>
            {candidates.map((store) => (
              <li key={store}>
                <button
                  type="button"
                  onClick={() => {
                    void onAdd(store);
                    setOpen(false);
                  }}
                  className={cn(
                    'block w-full rounded-sm px-3 py-2 text-left text-sm',
                    'text-text hover:bg-surface-2',
                  )}
                >
                  {PUBLISHING_STORE_LABELS[store]}
                </button>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
